import { Icon } from '@toggle/design-system';
import { formatPercentage } from '@toggle/helpers';
import { EventFeedEvent } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { getCardDate } from '~/views/dashboard/whats-new-widget/components/card-base/utils';
import { CardPill } from '~/views/stream/components/stream-card/card-pill/CardPill';
import { CardType } from '~/views/stream/components/stream-card/StreamCard';

import * as S from '../../OverviewEventFeed.styles';

export interface ScenarioRowProps {
  event: EventFeedEvent;
  isLast?: boolean;
  onMarkRead: (eventId: string) => void;
}

export const ScenarioRow = ({
  event,
  isLast,
  onMarkRead,
}: ScenarioRowProps) => {
  const { t } = useTranslation(['whatsNew', 'insights']);
  const { openDrawer } = useInsightDrawer();

  const median = (event.metadata.median as number) ?? 0;
  const horizon = event.metadata.horizon as string | undefined;
  const stars = event.metadata.stars as number | undefined;
  const episodeCount = (event.metadata.episode_count as number) ?? 0;
  const isBullish = median >= 0;
  const eventDate = new Date(event.created_at);

  const handleClick = () => {
    onMarkRead(event.id);
    const insightId = event.metadata.insight_id as string;
    openDrawer(insightId, { source: 'asset_overview' });
  };

  return (
    <S.FeedItemWrapper $isLast={isLast}>
      <S.FeedItem $isRead={event.read} onClick={handleClick}>
        <S.FeedItemHeader>
          <CardPill type={CardType.Scenario} isRead={event.read} />
        </S.FeedItemHeader>
        <S.InsightTitle $isRead={event.read}>{event.title}</S.InsightTitle>
        <S.StatsRow>
          <S.StatsContainer>
            {isBullish ? (
              <S.BullishValue>
                {formatPercentage(median, { suffix: '%', addPlus: true })}
              </S.BullishValue>
            ) : (
              <S.BearishValue>
                {formatPercentage(median, { suffix: '%', addPlus: true })}
              </S.BearishValue>
            )}
            {horizon && (
              <>
                <S.Dot />
                <S.StatValue>{horizon.toUpperCase()}</S.StatValue>
              </>
            )}
            {stars !== undefined && (
              <>
                <S.Dot />
                <S.StarsWrapper>
                  <Icon
                    iconName="Rating"
                    size={10}
                    fillColor="var(--icon-primary)"
                  />
                  <S.StarsLabel>{stars}</S.StarsLabel>
                </S.StarsWrapper>
              </>
            )}
            <S.VerticalDivider />
            <S.EpisodesLabel>
              {t('insights:insightCard.episode', { count: episodeCount })}
            </S.EpisodesLabel>
          </S.StatsContainer>
          <S.TimeAgo>{getCardDate(eventDate, t)}</S.TimeAgo>
        </S.StatsRow>
      </S.FeedItem>
    </S.FeedItemWrapper>
  );
};
